import { queryOptions } from '@tanstack/react-query'
import { z } from 'zod'

import { api, money, nullish, timestamp, ulid } from '@/shared/api'

/**
 * `GET /me` — **live**, against `samin-ex-core`'s users router. Both «پروفایل
 * من» and «اطلاعات شخصی» read this one call.
 *
 * The shape is core's `feat/me-profile` with `fix/me-unmasked-identity` on
 * top. Before them, `/me` sent only `id`, `phone`, `status`,
 * `status_label_fa`, `full_name`, `kyc_level` and `fee_tier_id`; every other
 * field here is new, and all of them are **required**: a core without them
 * fails the parse and both screens show their error, so core has to deploy
 * first. A default here would have the client invent a KYC level and a card
 * count.
 *
 * Every key is present in every response. The ones a level-0 account has no
 * value for — `full_name`, `avatar_initial`, `identity`, `residence` — are
 * explicit nulls, and `residence` is also null for anyone who never submitted
 * an address. The phone and the national ID are the account holder's own and
 * come whole, in ASCII digits; the screens print them in Persian digits, LTR.
 *
 * There is no `birth_date`, `email`, `security_score` or `preferences`, so
 * none is parsed and no screen has a row for them.
 */
export const meResponse = z.object({
  id: ulid,
  /** E.164. Not shown: the screens show `phone_local`. */
  phone: z.string(),
  /** `09124567890` — the number as the user dials it, eleven digits. */
  phone_local: z.string(),
  status: z.string(),
  status_label_fa: nullish(z.string()),
  full_name: z.string().nullable(),
  /** The first letter of `full_name`, null until the registry has supplied it. */
  avatar_initial: z.string().nullable(),
  kyc_level: z.number().int().nonnegative(),
  /** «احراز نشده» at level 0; null for a level core has no name for. */
  kyc_label_fa: z.string().nullable(),
  /** False at the top level and while gold evidence waits on a reviewer. */
  kyc_upgrade_available: z.boolean(),
  fee_tier_id: z.number().int().nonnegative(),
  fee_tier: z.object({
    id: z.number().int().nonnegative(),
    name_fa: z.string(),
    /** A fraction, not a percentage: `"0.005"` is 0.5%. */
    rate: money,
    label_fa: z.string(),
  }),
  /** Verified cards only — the hub labels it «کارت تایید شده». */
  bank_account_count: z.number().int().nonnegative(),
  /** A password is set, so sign-in is a code and then the password. */
  two_step_login: z.boolean(),
  /** Typed nullable in core, though every account has one. */
  member_since: timestamp.nullable(),
  /** `national_id` is the ten digits, `0023456561`. */
  identity: z.object({ national_id: z.string() }).nullable(),
  /**
   * Province and city only — the street and postal code never leave core.
   * `status` is parsed and not drawn: nothing reviews the address row, so it
   * is `pending` for every user who has one, gold users included.
   */
  residence: z
    .object({
      province: z.string(),
      city: z.string(),
      status: z.string(),
      status_label_fa: nullish(z.string()),
    })
    .nullable(),
})

export type Me = z.infer<typeof meResponse>

export const meKeys = {
  all: ['me'] as const,
  profile: () => [...meKeys.all, 'profile'] as const,
}

export const meQuery = queryOptions({
  queryKey: meKeys.profile(),
  queryFn: ({ signal }) => api.get('/me', { schema: meResponse, signal }),
})
